import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { apiService } from '../services/apiService';

const KPITimeseries = ({ showSnackbar }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [countries, setCountries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedChannel, setSelectedChannel] = useState('all');
  const [availableFiscalYears, setAvailableFiscalYears] = useState([]);

  useEffect(() => {
    loadData();
    loadKPIs(); // Cargar todos los KPIs inicialmente para extraer años fiscales
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar
  }, []);

  useEffect(() => {
    if (kpis.length > 0) {
      const fiscalYears = extractFiscalYears(kpis);
      setAvailableFiscalYears(fiscalYears);
      if (fiscalYears.length > 0 && !selectedFiscalYear) {
        setSelectedFiscalYear(fiscalYears[0]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo cuando cambian kpis
  }, [kpis]);

  useEffect(() => {
    if (selectedFiscalYear) {
      loadKPIs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- recargar al cambiar filtros
  }, [selectedCountry, selectedChannel]);

  // Función helper para determinar año fiscal de una fecha
  const getFiscalYear = (dateString) => {
    const d = new Date(dateString);
    const month = d.getMonth() + 1; // 1-12
    const year = d.getFullYear();
    if (month >= 4) {
      // Abril a Diciembre: FY actual-año siguiente
      return `FY ${String(year).slice(-2)}-${String(year + 1).slice(-2)}`;
    } else {
      // Enero a Marzo: FY año anterior-actual
      return `FY ${String(year - 1).slice(-2)}-${String(year).slice(-2)}`;
    }
  };

  // Extraer años fiscales únicos de los KPIs
  const extractFiscalYears = (kpiData) => {
    const fiscalYearsSet = new Set();
    kpiData.forEach(kpi => {
      if (kpi.month) {
        const fy = getFiscalYear(kpi.month);
        fiscalYearsSet.add(fy);
      }
    });
    return Array.from(fiscalYearsSet).sort().reverse(); // Más reciente primero
  };

  // Obtener rango de fechas para un año fiscal
  const getFiscalYearDates = (fiscalYearStr) => {
    // FY 25-26 -> abril 2025 a marzo 2026
    const match = fiscalYearStr.match(/FY (\d{2})-(\d{2})/);
    if (!match) return null;
    
    const startYear = parseInt('20' + match[1]);
    const endYear = parseInt('20' + match[2]);
    
    return {
      start: new Date(startYear, 3, 1), // Abril (mes 3, 0-indexed)
      end: new Date(endYear, 2, 31) // Marzo 31 (mes 2, 0-indexed)
    };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [countriesRes, channelsRes] = await Promise.all([
        apiService.getCountries({ for_kpis: true }),
        apiService.getChannels()
      ]);

      if (countriesRes.success) setCountries(countriesRes.countries);
      if (channelsRes.success) {
        const allChannels = channelsRes.channels || [];
        setChannels(allChannels.filter(ch => ['CH001', 'CH002'].includes(ch.id)));
      }
      
      setLoading(false);
    } catch (err) {
      setError('Error al cargar los datos');
      setLoading(false);
    }
  };

  const loadKPIs = async () => {
    try {
      const params = {};
      if (selectedCountry !== 'all') params.country_code = selectedCountry;
      if (selectedChannel !== 'all') params.channel_id = selectedChannel;

      const response = await apiService.getChannelKPIs(params);
      if (response.success) {
        setKpis(response.kpis);
      }
    } catch (err) {
      showSnackbar('Error al cargar KPIs', 'error');
    }
  };

  // Preparar datos para el gráfico
  const prepareChartData = () => {
    if (!selectedFiscalYear) return [];

    const fiscalDates = getFiscalYearDates(selectedFiscalYear);
    if (!fiscalDates) return [];

    // Filtrar KPIs del año fiscal seleccionado
    const filteredKPIs = kpis.filter(kpi => {
      if (!kpi.month) return false;
      const kpiDate = new Date(kpi.month);
      return kpiDate >= fiscalDates.start && kpiDate <= fiscalDates.end;
    });

    // Meses del año fiscal en orden
    const fiscalMonths = [
      { name: 'Abr', month: 4 },
      { name: 'May', month: 5 },
      { name: 'Jun', month: 6 },
      { name: 'Jul', month: 7 },
      { name: 'Ago', month: 8 },
      { name: 'Sep', month: 9 },
      { name: 'Oct', month: 10 },
      { name: 'Nov', month: 11 },
      { name: 'Dic', month: 12 },
      { name: 'Ene', month: 1 },
      { name: 'Feb', month: 2 },
      { name: 'Mar', month: 3 }
    ];

    // Crear estructura de datos por mes
    const chartData = fiscalMonths.map(({ name, month }) => {
      const year = month >= 4 ? fiscalDates.start.getFullYear() : fiscalDates.end.getFullYear();
      const monthDate = new Date(year, month - 1, 1);
      const monthKey = monthDate.toISOString().split('T')[0].substring(0, 7); // YYYY-MM

      // Buscar KPIs para este mes
      const monthKPIs = filteredKPIs.filter(kpi => {
        if (!kpi.month) return false;
        const kpiMonthKey = kpi.month.substring(0, 7);
        return kpiMonthKey === monthKey;
      });

      // Agregar valores (sumar si hay múltiples países/canales)
      const dataPoint = { name, month: monthKey };
      
      if (monthKPIs.length > 0) {
        dataPoint.reach = monthKPIs.reduce((sum, kpi) => sum + (kpi.reach || 0), 0);
        dataPoint.impressions = monthKPIs.reduce((sum, kpi) => sum + (kpi.impressions || 0), 0);
        dataPoint.engagement = monthKPIs.reduce((sum, kpi) => sum + (kpi.engagement || 0), 0);
        dataPoint.profile_visits = monthKPIs.reduce((sum, kpi) => sum + (kpi.profile_visits || 0), 0);
      } else {
        dataPoint.reach = 0;
        dataPoint.impressions = 0;
        dataPoint.engagement = 0;
        dataPoint.profile_visits = 0;
      }

      return dataPoint;
    });

    return chartData;
  };

  // Calcular crecimiento porcentual
  const calculateGrowth = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  // Obtener métricas de crecimiento para cada KPI
  const getGrowthMetrics = () => {
    const chartData = prepareChartData();
    if (chartData.length < 2) return null;

    const lastMonth = chartData[chartData.length - 1];
    const previousMonth = chartData[chartData.length - 2];

    return {
      reach: calculateGrowth(lastMonth.reach, previousMonth.reach),
      impressions: calculateGrowth(lastMonth.impressions, previousMonth.impressions),
      engagement: calculateGrowth(lastMonth.engagement, previousMonth.engagement),
      profile_visits: calculateGrowth(lastMonth.profile_visits, previousMonth.profile_visits)
    };
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('es-ES').format(Math.round(num));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const chartData = prepareChartData();
  const growthMetrics = getGrowthMetrics();

        return (
          <Box>
            <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>
              Timeseries de KPIs por Canal
            </Typography>

            {/* Filtros */}
            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Año Fiscal</InputLabel>
                  <Select
                    value={selectedFiscalYear}
                    label="Año Fiscal"
                    onChange={(e) => setSelectedFiscalYear(e.target.value)}
                  >
                    {availableFiscalYears.map((fy) => (
                      <MenuItem key={fy} value={fy}>
                        {fy}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filtrar por País</InputLabel>
                  <Select
                    value={selectedCountry}
                    label="Filtrar por País"
                    onChange={(e) => setSelectedCountry(e.target.value)}
                  >
                    <MenuItem value="all">Todos los países</MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filtrar por Canal</InputLabel>
                  <Select
                    value={selectedChannel}
                    label="Filtrar por Canal"
                    onChange={(e) => setSelectedChannel(e.target.value)}
                  >
                    <MenuItem value="all">Todos los canales</MenuItem>
                    {channels.map((channel) => (
                      <MenuItem key={channel.id} value={channel.id}>
                        {channel.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {!selectedFiscalYear ? (
              <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: 'center' }}>
                <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary">
                  Selecciona un año fiscal para ver la progresión de KPIs
                </Typography>
              </Paper>
            ) : (
              <>
                {/* Métricas de crecimiento */}
                {growthMetrics && (
                  <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 } }}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            Reach
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            {growthMetrics.reach !== null && (
                              <>
                                {growthMetrics.reach >= 0 ? (
                                  <TrendingUpIcon color="success" fontSize={isMobile ? 'small' : 'medium'} />
                                ) : (
                                  <TrendingDownIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
                                )}
                                <Typography
                                  variant={isMobile ? 'subtitle1' : 'h6'}
                                  color={growthMetrics.reach >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {growthMetrics.reach >= 0 ? '+' : ''}
                                  {growthMetrics.reach.toFixed(1)}%
                                </Typography>
                              </>
                            )}
                            {growthMetrics.reach === null && (
                              <Typography variant="body2" color="text.secondary">
                                Sin datos previos
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            Impresiones
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            {growthMetrics.impressions !== null && (
                              <>
                                {growthMetrics.impressions >= 0 ? (
                                  <TrendingUpIcon color="success" fontSize={isMobile ? 'small' : 'medium'} />
                                ) : (
                                  <TrendingDownIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
                                )}
                                <Typography
                                  variant={isMobile ? 'subtitle1' : 'h6'}
                                  color={growthMetrics.impressions >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {growthMetrics.impressions >= 0 ? '+' : ''}
                                  {growthMetrics.impressions.toFixed(1)}%
                                </Typography>
                              </>
                            )}
                            {growthMetrics.impressions === null && (
                              <Typography variant="body2" color="text.secondary">
                                Sin datos previos
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            Engagement
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            {growthMetrics.engagement !== null && (
                              <>
                                {growthMetrics.engagement >= 0 ? (
                                  <TrendingUpIcon color="success" fontSize={isMobile ? 'small' : 'medium'} />
                                ) : (
                                  <TrendingDownIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
                                )}
                                <Typography
                                  variant={isMobile ? 'subtitle1' : 'h6'}
                                  color={growthMetrics.engagement >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {growthMetrics.engagement >= 0 ? '+' : ''}
                                  {growthMetrics.engagement.toFixed(1)}%
                                </Typography>
                              </>
                            )}
                            {growthMetrics.engagement === null && (
                              <Typography variant="body2" color="text.secondary">
                                Sin datos previos
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Card>
                        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Typography color="textSecondary" gutterBottom variant="body2">
                            Visitas Perfil
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            {growthMetrics.profile_visits !== null && (
                              <>
                                {growthMetrics.profile_visits >= 0 ? (
                                  <TrendingUpIcon color="success" fontSize={isMobile ? 'small' : 'medium'} />
                                ) : (
                                  <TrendingDownIcon color="error" fontSize={isMobile ? 'small' : 'medium'} />
                                )}
                                <Typography
                                  variant={isMobile ? 'subtitle1' : 'h6'}
                                  color={growthMetrics.profile_visits >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {growthMetrics.profile_visits >= 0 ? '+' : ''}
                                  {growthMetrics.profile_visits.toFixed(1)}%
                                </Typography>
                              </>
                            )}
                            {growthMetrics.profile_visits === null && (
                              <Typography variant="body2" color="text.secondary">
                                Sin datos previos
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Gráfico de líneas */}
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom>
                    Progresión de KPIs - {selectedFiscalYear}
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  formatter={(value, name) => {
                    const labels = {
                      reach: 'Reach',
                      impressions: 'Impresiones',
                      engagement: 'Engagement',
                      profile_visits: 'Visitas Perfil'
                    };
                    return [formatNumber(value), labels[name] || name];
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="reach"
                  stroke="#004a8F"
                  strokeWidth={2}
                  name="Reach"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="impressions"
                  stroke="#E70033"
                  strokeWidth={2}
                  name="Impresiones"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="engagement"
                  stroke="#3373b3"
                  strokeWidth={2}
                  name="Engagement"
                  dot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="profile_visits"
                  stroke="#4CAF50"
                  strokeWidth={2}
                  name="Visitas Perfil"
                  dot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default KPITimeseries;
